/**
 * TcAgreementPage.tsx — Provider T&C agreement acceptance screen.
 *
 * Story 7.7: Provider T&C Agreement System — Onboarding Hard Gate.
 *
 * Fetches the current active T&C document on mount and requires the provider to
 * scroll to the bottom before enabling the "I Accept" button.
 *
 * AC: 1, 4 — route guard renders this; acceptance calls POST tc-agreements.
 * All colors via Tailwind design token classes — no hardcoded hex values.
 * Uses @nrx/ui Button/Card primitives.
 */

import type { components } from '@nrx/betty-contracts/codegen/ts/betty-integrations';
import { Button } from '@nrx/ui';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { JSX } from 'react';
import { useNavigate } from 'react-router-dom';

// ─── Types ────────────────────────────────────────────────────────────────────

type TcDocument = components['schemas']['TermsAndConditionsDocument'];
type ProblemDetail = components['schemas']['ProblemDetail'];

interface TcAgreementPageProps {
  providerId: string;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function TcAgreementPage({ providerId }: TcAgreementPageProps): JSX.Element {
  const navigate = useNavigate();

  const [document, setDocument] = useState<TcDocument | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const [isAccepting, setIsAccepting] = useState(false);
  const [acceptError, setAcceptError] = useState<ProblemDetail | null>(null);

  // Sentinel div at the end of the T&C content — IntersectionObserver watches it.
  const sentinelRef = useRef<HTMLDivElement>(null);

  // Fetch current T&C document on mount
  useEffect(() => {
    let cancelled = false;

    async function loadDocument() {
      try {
        const res = await fetch('/v1/providers/tc/current', {
          headers: { Accept: 'application/json' },
          credentials: 'include',
        });

        if (!res.ok) {
          const problem = (await res.json()) as ProblemDetail;
          if (!cancelled) {
            setLoadError(problem.detail ?? 'Failed to load T&C document.');
          }
          return;
        }

        const doc = (await res.json()) as TcDocument;
        if (!cancelled) {
          setDocument(doc);
        }
      } catch {
        if (!cancelled) {
          setLoadError('Network error. Please try again.');
        }
      }
    }

    void loadDocument();
    return () => {
      cancelled = true;
    };
  }, []);

  // IntersectionObserver: enable Accept button when sentinel is visible
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry?.isIntersecting) {
          setHasScrolledToBottom(true);
          observer.disconnect();
        }
      },
      { threshold: 0.5 },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [document]); // Re-run when document is loaded

  const handleAccept = useCallback(async () => {
    if (!document || isAccepting) return;

    setIsAccepting(true);
    setAcceptError(null);

    try {
      const res = await fetch(`/v1/providers/${providerId}/tc-agreements`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ documentId: document.id }),
      });

      if (res.ok) {
        // 200 = first acceptance or idempotent repeat → proceed to dashboard
        void navigate('/dashboard');
        return;
      }

      // Parse the RFC 9457 problem detail
      const problem = (await res.json()) as ProblemDetail;

      if (res.status === 409) {
        // 409 = "tc-document-not-active": the document we tried to accept is no longer active
        // (a new version was published between page load and submit). Re-fetch the current
        // document and re-render with "terms have been updated" message so the provider
        // can read and accept the new version. Never navigate on 409 — it is an error.
        try {
          const freshDocRes = await fetch('/v1/providers/tc/current', {
            headers: { Accept: 'application/json' },
            credentials: 'include',
          });
          if (freshDocRes.ok) {
            const freshDoc = (await freshDocRes.json()) as typeof document;
            setDocument(freshDoc);
            setHasScrolledToBottom(false); // require re-read of updated terms
          }
        } catch {
          // Best-effort refetch — if it fails, fall through to show the error below
        }
        setAcceptError({
          ...problem,
          detail:
            problem.detail ??
            'The Terms & Conditions have been updated. Please review and accept the latest version.',
          title: problem.title ?? 'Terms & Conditions Updated',
        });
        return;
      }

      setAcceptError(problem);
    } catch {
      setAcceptError({
        type: 'https://errors.nrx.com/network-error',
        title: 'Network error',
        status: 0,
        detail: 'Could not connect to server. Please try again.',
        correlationId: '',
      });
    } finally {
      setIsAccepting(false);
    }
  }, [document, isAccepting, providerId, navigate]);

  // ─── Render states ──────────────────────────────────────────────────────────

  if (loadError) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4">
        <div className="max-w-lg rounded-lg bg-white p-8 shadow-md">
          <h1 className="text-xl font-semibold text-red-700">
            Could not load Terms &amp; Conditions
          </h1>
          <p className="mt-2 text-gray-600">{loadError}</p>
        </div>
      </div>
    );
  }

  if (!document) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <p className="text-gray-500">Loading Terms &amp; Conditions…</p>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-gray-50 px-4 py-8">
      <div className="mx-auto w-full max-w-3xl">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-semibold text-gray-900">Terms &amp; Conditions</h1>
          <p className="mt-1 text-sm text-gray-500">
            Version {document.version} — Effective{' '}
            {new Date(document.effectiveDate).toLocaleDateString()}
          </p>
          <p className="mt-2 text-sm text-gray-600">
            Please read the following Terms &amp; Conditions. Scroll to the bottom to enable
            acceptance.
          </p>
        </div>

        {/* T&C content — scrollable container */}
        <div className="mb-6 h-96 overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-sm">
          <pre className="whitespace-pre-wrap p-6 font-sans text-sm text-gray-800 leading-relaxed">
            {document.content}
          </pre>
          {/* Scroll-to-bottom sentinel */}
          <div ref={sentinelRef} aria-hidden="true" className="h-1" />
        </div>

        {/* Scroll hint */}
        {!hasScrolledToBottom && (
          <p className="mb-4 text-center text-sm text-gray-500">
            Scroll to the bottom to enable acceptance.
          </p>
        )}

        {/* Error display */}
        {acceptError && (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-4" role="alert">
            <p className="font-medium text-red-700">{acceptError.title}</p>
            <p className="mt-1 text-sm text-red-600">{acceptError.detail}</p>
            {acceptError.correlationId && (
              <p className="mt-2 text-xs text-gray-400">Reference: {acceptError.correlationId}</p>
            )}
          </div>
        )}

        {/* Accept button */}
        <div className="flex justify-center">
          <Button
            tone="primary"
            disabled={!hasScrolledToBottom || isAccepting}
            onClick={() => void handleAccept()}
          >
            {isAccepting ? 'Recording acceptance…' : 'I Accept'}
          </Button>
        </div>
      </div>
    </div>
  );
}
